import React, { useState, useEffect } from 'react';
import axios from 'axios';

interface AnalyticsData {
    id: string;
    name: string;
    visitors: number;
    leads: number;
    events: number;
}

interface Props {
    landingPageId: string;
    apiBase: string;
}

export default function Analytics({ landingPageId, apiBase }: Props) {
    const [analytics, setAnalytics] = useState<AnalyticsData[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchAnalytics();
        const interval = setInterval(fetchAnalytics, 5000); // Refresh every 5 seconds
        return () => clearInterval(interval);
    }, [landingPageId]);

    const fetchAnalytics = async () => {
        try {
            const response = await axios.get(`${apiBase}/admin/analytics/${landingPageId}`);
            setAnalytics(response.data || []);
        } catch (error) {
            console.error('Error fetching analytics:', error);
        } finally {
            setLoading(false);
        }
    };

    const totalVisitors = analytics.reduce((sum, v) => sum + v.visitors, 0);
    const totalLeads = analytics.reduce((sum, v) => sum + v.leads, 0);
    const conversionRate = totalVisitors > 0 ? ((totalLeads / totalVisitors) * 100).toFixed(2) : '0';

    if (loading) return <div className="loading">Loading analytics...</div>;

    return (
        <div className="analytics">
            <div className="section-header">
                <h2>Performance Analytics</h2>
                <button className="btn btn-small" onClick={fetchAnalytics}>
                    🔄 Refresh
                </button>
            </div>

            {/* Summary Cards */}
            <div className="summary-cards">
                <div className="metric-card">
                    <div className="label">Total Visitors</div>
                    <div className="value">{totalVisitors.toLocaleString()}</div>
                </div>
                <div className="metric-card">
                    <div className="label">Total Leads</div>
                    <div className="value">{totalLeads.toLocaleString()}</div>
                </div>
                <div className="metric-card">
                    <div className="label">Conversion Rate</div>
                    <div className="value">{conversionRate}%</div>
                </div>
            </div>

            {/* Variants Performance */}
            <div className="variants-performance">
                <h3>Variant Performance</h3>
                {analytics.length === 0 ? (
                    <div className="empty-state">
                        <p>No data yet. Variants need visitors first!</p>
                    </div>
                ) : (
                    <div className="performance-grid">
                        {analytics.map((variant) => {
                            const conversionRate =
                                variant.visitors > 0
                                    ? ((variant.leads / variant.visitors) * 100).toFixed(2)
                                    : '0';
                            const percentOfTotal =
                                totalVisitors > 0 ? ((variant.visitors / totalVisitors) * 100).toFixed(1) : '0';

                            return (
                                <div key={variant.id} className="performance-card">
                                    <h4>{variant.name}</h4>

                                    <div className="stat-row">
                                        <span className="label">Visitors:</span>
                                        <span className="value">{variant.visitors}</span>
                                        <span className="percentage">({percentOfTotal}%)</span>
                                    </div>

                                    <div className="progress-bar">
                                        <div
                                            className="progress-fill"
                                            style={{ width: `${percentOfTotal}%` }}
                                        ></div>
                                    </div>

                                    <div className="stat-row">
                                        <span className="label">Leads:</span>
                                        <span className="value">{variant.leads}</span>
                                    </div>

                                    <div className="stat-row">
                                        <span className="label">Conversion:</span>
                                        <span className="value">{conversionRate}%</span>
                                    </div>

                                    {variant.leads > 0 && (
                                        <div className="stat-row highlight">
                                            <span className="label">Cost per Lead:</span>
                                            <span className="value">
                                                ${(1 / variant.leads).toFixed(2)} (relative)
                                            </span>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Detailed Table */}
            {analytics.length > 0 && (
                <div className="detailed-table">
                    <h3>Detailed Comparison</h3>
                    <table>
                        <thead>
                            <tr>
                                <th>Variant Name</th>
                                <th>Visitors</th>
                                <th>% of Traffic</th>
                                <th>Leads</th>
                                <th>Conversion Rate</th>
                            </tr>
                        </thead>
                        <tbody>
                            {analytics.map((variant) => {
                                const conversionRate =
                                    variant.visitors > 0
                                        ? ((variant.leads / variant.visitors) * 100).toFixed(2)
                                        : '0';
                                const percentOfTotal =
                                    totalVisitors > 0 ? ((variant.visitors / totalVisitors) * 100).toFixed(1) : '0';

                                return (
                                    <tr key={variant.id}>
                                        <td>
                                            <strong>{variant.name}</strong>
                                        </td>
                                        <td>{variant.visitors}</td>
                                        <td>{percentOfTotal}%</td>
                                        <td>{variant.leads}</td>
                                        <td className="highlight">{conversionRate}%</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
